import { useState, useCallback } from "react";
import { FiExternalLink, FiDownload } from "react-icons/fi";
import { Spinner } from "@chakra-ui/react";
import { cn } from "utils/helpers";
import SkeletonBase from "newui/components/Skeleton";
import { useRfxIndividualResponseExport } from "newui/features/RfxReports";
import { useNavigationPaths } from "newui/features/Auth/hooks/useNavigationPaths";
import type { SupplierEvaluationSummary, SupplierEvaluationStatus } from "../../../types/scoring.type";
import FallbackImage from "newui/components/FallbackImage/FallbackImage";
import { Avatar } from "components/RFQ/Avatar";
import { ProfileAvatar } from "newui/components/ProfileAvatar/ProfileAvatar";
import { CompanyLogoDisplay } from "newui/features/Company/components/CompanyLogo";

interface SupplierResponseTableProps {
	evaluatorId?: number;
	isFinalMode?: boolean;
	isLoading: boolean;
	readOnly?: boolean;
	rfxId: number;
	suppliers: SupplierEvaluationSummary[];
	onSelectSupplier: (inviteId: number) => void;
}

const getStatusBadgeClasses = (status: SupplierEvaluationStatus): string => {
	switch (status) {
		case "done":
			return "bg-green-100 text-green-700";
		case "in_progress":
			return "bg-blue-100 text-blue-700";
		case "not_started":
			return "bg-gray-100 text-gray-600";
		default:
			return "bg-gray-100 text-gray-600";
	}
};

const getStatusLabel = (status: SupplierEvaluationStatus): string => {
	switch (status) {
		case "done":
			return "Done";
		case "in_progress":
			return "In Progress";
		case "not_started":
			return "Not Started";
		default:
			return status;
	}
};

const formatDate = (dateStr: string | null): string => {
	if (!dateStr) {
		return "—";
	}
	const date = new Date(dateStr);
	return date.toLocaleDateString("en-AU", {
		day: "2-digit",
		month: "short",
		year: "numeric",
	});
};

export const SupplierResponseTable: React.FC<SupplierResponseTableProps> = ({
	suppliers,
	isLoading,
	rfxId,
	onSelectSupplier,
	readOnly = false,
	isFinalMode = false,
}) => {
	const { clientPaths } = useNavigationPaths();
	const [pdfLoadingId, setPdfLoadingId] = useState<number | null>(null);
	const { mutateAsync: exportResponse } = useRfxIndividualResponseExport();

	const handlePdfDownload = useCallback(
		(e: React.MouseEvent, inviteId: number) => {
			e.stopPropagation();
			setPdfLoadingId(inviteId);
			exportResponse({ rfxId, inviteId }).finally(() => {
				setPdfLoadingId(null);
			});
		},
		[rfxId, exportResponse]
	);
	if (isLoading) {
		return (
			<div className="space-y-3" data-testid="supplier-table-loading">
				{[1, 2, 3].map((i) => (
					<SkeletonBase key={i} startColor="lightgray" width="100%" height="48px" className="!rounded-lg" />
				))}
			</div>
		);
	}

	if (suppliers.length === 0) {
		return (
			<div
				className="rounded-lg border border-dashed border-gray-300 p-8 text-center text-gray-400 text-sm"
				data-testid="supplier-table-empty"
			>
				No supplier responses available for scoring.
			</div>
		);
	}

	return (
		<div className="w-full overflow-x-auto" data-testid="supplier-response-table">
			<table className="w-full min-w-[800px] border-collapse text-sm">
				<thead>
					<tr className="border-b border-gray-200 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
						<th className="px-3 py-3">Supplier Name</th>
						<th className="px-3 py-3">Status</th>
						<th className="px-3 py-3">% Complete</th>
						<th className="px-3 py-3">Date Started</th>
						<th className="px-3 py-3">Date Completed</th>
						<th className="px-3 py-3 text-right">Actions</th>
					</tr>
				</thead>
				<tbody>
					{suppliers.map((supplier, index) => (
						<tr
							key={supplier.inviteId}
							className={cn(
								"border-b border-gray-100 transition-colors",
								readOnly
									? "hover:bg-gray-50"
									: "hover:bg-blue-50 cursor-pointer"
							)}
							onClick={() => {
								if (!readOnly) {
									onSelectSupplier(supplier.inviteId);
								}
							}}
							data-testid={`supplier-row-${supplier.inviteId}`}
							role={readOnly ? undefined : "button"}
							tabIndex={readOnly ? undefined : 0}
							onKeyDown={(e) => {
								if (!readOnly && (e.key === "Enter" || e.key === " ")) {
									e.preventDefault();
									onSelectSupplier(supplier.inviteId);
								}
							}}
						>
							<td className="px-3 py-3 font-medium text-gray-900">
								<div className="flex flex-row items-center gap-3">
									<CompanyLogoDisplay className={`size-[50px] p-1 object-contain`} data-testid={`supplier-logo-${supplier.inviteId}`} supplierLogoUrl={supplier.supplierLogo} supplierName={supplier.supplierName} size="custom" displayStyle="normal" />
									{supplier.supplierName}
								</div>
							</td>
							<td className="px-3 py-3">
								<span
									className={cn(
										"inline-flex rounded-full px-2 py-0.5 text-xs font-medium",
										getStatusBadgeClasses(
											(isFinalMode && supplier.finalStatus) ? supplier.finalStatus : supplier.status
										)
									)}
								>
									{getStatusLabel(
										(isFinalMode && supplier.finalStatus) ? supplier.finalStatus : supplier.status
									)}
								</span>
							</td>
							<td className="px-3 py-3 text-gray-600">
								{(isFinalMode && supplier.finalPercentComplete !== null)
									? supplier.finalPercentComplete
									: supplier.percentComplete}%
							</td>
							<td className="px-3 py-3 text-gray-600">
								{formatDate(supplier.startedAt)}
							</td>
							<td className="px-3 py-3 text-gray-600">
								{formatDate(supplier.completedAt)}
							</td>
							<td className="px-3 py-3" data-testid={index === 0 ? "supplier-response-actions" : ""}>
								<div className="flex items-center justify-end gap-2">
									<a
										href={clientPaths.viewRfxSubmission(rfxId, supplier.inviteId)}
										target="_blank"
										rel="noopener noreferrer"
										onClick={(e) => e.stopPropagation()}
										className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50 transition-colors"
										data-testid={`view-submission-${supplier.inviteId}`}
									>
										<FiExternalLink size={12} />
										View
									</a>
									<button
										type="button"
										onClick={(e) => handlePdfDownload(e, supplier.inviteId)}
										disabled={pdfLoadingId === supplier.inviteId}
										className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
										data-testid={`download-pdf-${supplier.inviteId}`}
									>
										{pdfLoadingId === supplier.inviteId ? (
											<Spinner size="xs" />
										) : (
											<FiDownload size={12} />
										)}
										PDF
									</button>
								</div>
							</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
};
